/**
 * Product Card Image Cycling — desktop + mobile.
 *
 * Minimal diff on top of the original mobile-only script:
 *   - The `if (window.innerWidth <= 749)` guards are removed so init runs
 *     at all widths.
 *   - Left/right arrow buttons are injected as direct children of
 *     `.card-wrapper` — same DOM location the original play button used,
 *     which is what keeps them clickable instead of falling through to the
 *     card's product-link overlay.
 *   - Arrow + indicator positions are set from the image dimensions using
 *     the same `getBoundingClientRect` pattern the original relied on.
 *   - Touch swipe is wired on `.card__media .media`.
 *   - Indicator pips are shown from the start (not only after the first click).
 *   - `window.cycleProductImages` is preserved as a back-compat shim for any
 *     existing Liquid that calls it.
 *
 * Cycling mechanism is unchanged from the original: images are already in
 * the DOM, we flip `mobile-image-visible` / `mobile-image-hidden` classes.
 * No extra network requests on cycle.
 */

(function () {
  'use strict';

  var SWIPE_THRESHOLD_PX = 40;
  var SWIPE_VELOCITY_PX_MS = 0.3;

  var ARROW_SVG_PREV =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';
  var ARROW_SVG_NEXT =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  function getCurrentIndex(wrapper) {
    return parseInt(wrapper.dataset.currentImageIndex || '0', 10);
  }

  function getTotalImages(wrapper) {
    return parseInt(wrapper.dataset.totalImages || '0', 10);
  }

  function updateArrowVisibility(wrapper, index, total) {
    var prev = wrapper.querySelector('.image-cycling-arrow--prev');
    var next = wrapper.querySelector('.image-cycling-arrow--next');
    if (prev) prev.classList.toggle('is-visible', index > 0);
    if (next) next.classList.toggle('is-visible', index < total - 1);
  }

  function goTo(wrapper, targetIndex) {
    var total = getTotalImages(wrapper);
    if (total < 2) return;

    if (targetIndex < 0) targetIndex = 0;
    if (targetIndex >= total) targetIndex = total - 1;

    var current = getCurrentIndex(wrapper);
    if (targetIndex === current) return;

    var mediaContainer = wrapper.querySelector('.card__media .media');
    if (!mediaContainer) return;
    var images = mediaContainer.querySelectorAll('img[data-image-index]');

    images.forEach(function (img, i) {
      if (i === targetIndex) {
        img.classList.remove('mobile-image-hidden');
        img.classList.add('mobile-image-visible');
      } else {
        img.classList.add('mobile-image-hidden');
        img.classList.remove('mobile-image-visible');
      }
    });

    var dots = wrapper.querySelectorAll('.image-cycling-indicators .indicator-dot');
    dots.forEach(function (dot, i) {
      dot.classList.toggle('active', i === targetIndex);
    });

    wrapper.dataset.currentImageIndex = String(targetIndex);
    updateArrowVisibility(wrapper, targetIndex, total);
  }

  function buildArrow(direction) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'image-cycling-arrow image-cycling-arrow--' + direction;
    btn.setAttribute('aria-label', direction === 'prev' ? 'Previous image' : 'Next image');
    btn.innerHTML = direction === 'prev' ? ARROW_SVG_PREV : ARROW_SVG_NEXT;
    return btn;
  }

  function buildIndicators(count) {
    var wrap = document.createElement('div');
    wrap.className = 'image-cycling-indicators visible';
    wrap.setAttribute('role', 'tablist');
    wrap.setAttribute('aria-label', 'Product images');
    for (var i = 0; i < count; i++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'indicator-dot' + (i === 0 ? ' active' : '');
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', 'Go to image ' + (i + 1));
      dot.dataset.index = String(i);
      wrap.appendChild(dot);
    }
    return wrap;
  }

  function setupSwipe(wrapper, mediaContainer) {
    var startX = 0;
    var startY = 0;
    var startTime = 0;
    var tracking = false;
    var locked = null; // 'h' | 'v' | null

    mediaContainer.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      tracking = true;
      locked = null;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startTime = Date.now();
    }, { passive: true });

    mediaContainer.addEventListener('touchmove', function (e) {
      if (!tracking) return;
      if (locked === null) {
        var dx = e.touches[0].clientX - startX;
        var dy = e.touches[0].clientY - startY;
        if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
          locked = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
          if (locked === 'v') tracking = false;
        }
      }
    }, { passive: true });

    mediaContainer.addEventListener('touchend', function (e) {
      if (!tracking) return;
      tracking = false;
      var dx = e.changedTouches[0].clientX - startX;
      var dy = e.changedTouches[0].clientY - startY;
      var dt = Math.max(1, Date.now() - startTime);
      if (
        Math.abs(dx) > Math.abs(dy) &&
        (Math.abs(dx) > SWIPE_THRESHOLD_PX || Math.abs(dx) / dt > SWIPE_VELOCITY_PX_MS)
      ) {
        var cur = getCurrentIndex(wrapper);
        goTo(wrapper, dx < 0 ? cur + 1 : cur - 1);
      }
    }, { passive: true });

    mediaContainer.addEventListener('touchcancel', function () {
      tracking = false;
      locked = null;
    }, { passive: true });
  }

  /* Position all controls in a single row near the bottom of the image:
     prev arrow at bottom-left, next arrow at bottom-right, pips centred.
     All three sit on the same baseline so the image stays uncluttered.

     Batch 4 (26 Oct 2026): HGN cards (`.hgn-card.card-wrapper`, every
     card-product render) are skipped: hgn-card-product.css puts their
     arrows and pips in the card's own grid row with `top: auto
     !important`, so the inline tops written here never applied. Each
     call still read two rects straight after the previous card's writes,
     one forced layout per card, at DOMContentLoaded, on every image load
     and on every appended card (recorder: 83 slow views, forced layout
     up to 11.6 s). Any other `.card-wrapper` is positioned as before, but
     in one pass per frame: all reads first, then all writes. */
  function needsPositioning(wrapper) {
    return !(wrapper.classList && wrapper.classList.contains('hgn-card'));
  }

  function measureControls(wrapper) {
    var mediaDiv = wrapper.querySelector('.card__media');
    if (!mediaDiv) return null;

    var mediaRect = mediaDiv.getBoundingClientRect();
    if (!mediaRect.height) return null; // image hasn't laid out yet

    var wrapperRect = wrapper.getBoundingClientRect();
    var relativeTop = mediaRect.top - wrapperRect.top;
    return { wrapper: wrapper, relativeBottom: relativeTop + mediaRect.height };
  }

  function applyControls(plan, isMobile) {
    var wrapper = plan.wrapper;
    var relativeBottom = plan.relativeBottom;
    var ARROW_SIZE = isMobile ? 28 : 36;
    var PIP_HEIGHT = isMobile ? 20 : 26;
    var ARROW_BOTTOM_INSET = 6; // px from the image bottom to the arrow's bottom edge
    var PIP_BOTTOM_INSET = 2;   // pips sit a touch lower — right against the image edge

    var prev = wrapper.querySelector('.image-cycling-arrow--prev');
    var next = wrapper.querySelector('.image-cycling-arrow--next');
    var arrowTop = relativeBottom - ARROW_BOTTOM_INSET - ARROW_SIZE;
    if (prev) {
      prev.style.top = arrowTop + 'px';
      prev.style.bottom = 'auto';
    }
    if (next) {
      next.style.top = arrowTop + 'px';
      next.style.bottom = 'auto';
    }

    var indicators = wrapper.querySelector('.image-cycling-indicators');
    if (indicators) {
      indicators.style.top = (relativeBottom - PIP_BOTTOM_INSET - PIP_HEIGHT) + 'px';
      indicators.style.bottom = 'auto';
    }
  }

  var pending = [];
  var pendingFrame = false;
  function positionControls(wrapper) {
    if (!needsPositioning(wrapper)) return;
    if (pending.indexOf(wrapper) === -1) pending.push(wrapper);
    if (pendingFrame) return;
    pendingFrame = true;
    (window.requestAnimationFrame || function (f) { return setTimeout(f, 16); })(function () {
      pendingFrame = false;
      var list = pending;
      pending = [];
      var isMobile = window.matchMedia('(max-width: 749px)').matches;
      var plans = [];
      list.forEach(function (w) { if (document.contains(w)) { var p = measureControls(w); if (p) plans.push(p); } });
      plans.forEach(function (p) { applyControls(p, isMobile); });
    });
  }

  function positionAll() {
    document.querySelectorAll('.card-wrapper').forEach(positionControls);
  }

  function setupCard(wrapper) {
    if (wrapper.dataset.cardCyclingInitialized === 'true') return;

    var mediaContainer = wrapper.querySelector('.card__media .media');
    if (!mediaContainer) return;

    var images = mediaContainer.querySelectorAll('img[data-image-index]');
    if (images.length < 2) return;

    wrapper.dataset.cardCyclingInitialized = 'true';
    wrapper.dataset.currentImageIndex = '0';
    wrapper.dataset.totalImages = String(images.length);

    // Same as the original mobile init: first image visible, rest hidden.
    images.forEach(function (img, i) {
      if (i === 0) {
        img.classList.add('mobile-image-visible');
      } else {
        img.classList.add('mobile-image-hidden');
      }
    });

    // Indicators: re-use whatever the Liquid rendered, else build a fresh
    // set. Either way, ensure they're a DIRECT child of `.card-wrapper`
    // (outside any product-link anchor) so they stay clickable.
    var indicators = wrapper.querySelector('.image-cycling-indicators');
    if (indicators) {
      if (indicators.parentElement !== wrapper) wrapper.appendChild(indicators);

      var existingDots = indicators.querySelectorAll('.indicator-dot');
      if (existingDots.length !== images.length) {
        indicators.innerHTML = '';
        for (var d = 0; d < images.length; d++) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'indicator-dot' + (d === 0 ? ' active' : '');
          dot.setAttribute('role', 'tab');
          dot.setAttribute('aria-label', 'Go to image ' + (d + 1));
          dot.dataset.index = String(d);
          indicators.appendChild(dot);
        }
      } else {
        existingDots.forEach(function (el, i) {
          el.classList.toggle('active', i === 0);
          el.dataset.index = String(i);
        });
      }
      indicators.classList.add('visible');
    } else {
      indicators = buildIndicators(images.length);
      wrapper.appendChild(indicators);
    }

    // Arrows: append as DIRECT children of `.card-wrapper` (same location
    // the original play button sat). This keeps them outside the card's
    // overlay anchor so the click doesn't fall through to the product link.
    var prevBtn = buildArrow('prev');
    var nextBtn = buildArrow('next');
    wrapper.appendChild(prevBtn);
    wrapper.appendChild(nextBtn);

    updateArrowVisibility(wrapper, 0, images.length);

    prevBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      goTo(wrapper, getCurrentIndex(wrapper) - 1);
    });
    nextBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      goTo(wrapper, getCurrentIndex(wrapper) + 1);
    });

    indicators.addEventListener('click', function (e) {
      var target = e.target.closest('.indicator-dot');
      if (!target) return;
      e.preventDefault();
      e.stopPropagation();
      goTo(wrapper, parseInt(target.dataset.index || '0', 10));
    });

    setupSwipe(wrapper, mediaContainer);

    // Position the controls now and again once images finish loading
    // (image dimensions can't be trusted until then). HGN cards: their CSS
    // places the controls, so neither runs (batch 4).
    if (!needsPositioning(wrapper)) return;
    positionControls(wrapper);
    mediaContainer.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', function () { positionControls(wrapper); }, { once: true });
    });
  }

  function setupAll(root) {
    (root || document).querySelectorAll('.card-wrapper').forEach(setupCard);
  }

  function watchForNewCards() {
    if (!('MutationObserver' in window)) return;
    /* #ProductGridContainer first (2026-10-02): facets.js replaces its
       children on every filter / sort, so observing the original
       #product-grid stopped at the first swap and swapped-in (and
       infinite-scroll appended) cards never got cycling. The container
       survives the swap. */
    var grid =
      document.getElementById('ProductGridContainer') ||
      document.querySelector('#product-grid') ||
      document.querySelector('.collection') ||
      document.querySelector('[data-section-type="collection"]') ||
      document.body;
    if (!grid) return;

    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        for (var j = 0; j < m.addedNodes.length; j++) {
          var node = m.addedNodes[j];
          if (node.nodeType !== 1) continue;
          if (node.classList && node.classList.contains('card-wrapper')) {
            setupCard(node);
          } else if (node.querySelectorAll) {
            node.querySelectorAll('.card-wrapper').forEach(setupCard);
          }
        }
      }
    });
    mo.observe(grid, { childList: true, subtree: true });
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  ready(function () {
    setupAll();
    watchForNewCards();

    // Reposition on resize (debounced), matching the original's pattern.
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(positionAll, 150);
    });
  });

  // Back-compat: keep the original global so any Liquid still calling
  // onclick="cycleProductImages(this)" works. Delegates into the new flow.
  window.cycleProductImages = function (button) {
    var wrapper = button && button.closest && button.closest('.card-wrapper');
    if (!wrapper) return;
    if (wrapper.dataset.cardCyclingInitialized !== 'true') setupCard(wrapper);
    var total = getTotalImages(wrapper);
    if (total < 2) return;
    goTo(wrapper, (getCurrentIndex(wrapper) + 1) % total);
  };
})();
